import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { ready } from 'shared/ready'

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]
const MAX_SIZE = 10 * 1024 * 1024 // 10MB in bytes

class UploadModalSettings {
  constructor() {
    this.fileData = undefined

    this.$modalUploadContainer = document.getElementById('upload-modal-container')
    this.$modalUploadRetry = document.getElementById('modal_upload__button-retry')
    this.$formConfirmEmail = document.getElementById('modal_upload__confirm-email_form')

    /* MODAL STEPS */
    this.$stepUploadFailed = document.getElementById('upload-failed-container')
    this.$stepAddEmail = document.getElementById('upload-completed-container')
    this.$stepUploadData = document.getElementById('upload_send__api-container')

    this.fillEventListeners()
    this.fillHomepageEventListeners()
  }

  openModal() {
    this.$modalUploadContainer.classList.add('is-animated', 'is-visible')
  }

  closeModal() {
    this.$modalUploadContainer.classList.remove('is-animated', 'is-visible')
    this.$stepAddEmail.classList.add('hidden')
    this.$stepUploadData.classList.add('hidden')
    this.$stepUploadFailed.classList.add('hidden')
  }

  checkFile(file) {
    if (!file) return { success: false, message: 'no file' }
    if (!ALLOWED_TYPES.includes(file.type))
      return { success: false, message: 'file not valid type' }
    if (file.size > MAX_SIZE) return { success: false, message: 'file over 10mb' }

    return { success: true }
  }

  resetUploadModalSettings() {
    const screen = this.visibleScreen()
    trackInternalEvent('click_exit_upload_modal', {
      ...this.getLabelAndBlock(),
      screen,
    })
    this.fileData = undefined
    this.closeModal()
  }

  async sendUploadDetails(e) {
    e.preventDefault()
    const email = e.target.email.value || undefined

    try {
      // call the api to create the email
      const createUserResponse = await this.createUser(email)
      if (!createUserResponse) return false

      // move to upload modal
      this.showLastStep()

      // call the api to upload
      const uploadResponse = await this.uploadCV()
      if (!uploadResponse) return false

      // all good call the api to upload the data associated to the email.
      window.location.replace(uploadResponse)
    } catch (error) {
      console.error('Upload error:', error)
      this.prepareErrorModal()
      return false
    }
  }

  /* API calls */
  async createUser(email) {
    const response = await fetch('/api/builder/resume_upload/register', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email,
      }),
    })

    if (response.status !== 200) return false
    const data = await response.json()

    return data.success
  }

  async uploadCV() {
    const form = new FormData()
    form.append('resume', this.fileData)

    trackInternalEvent('submit_sign_up_upload', this.getLabelAndBlock())

    const response = await fetch('/api/builder/resume_upload/upload_and_parse', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      body: form,
    })

    if (response.status !== 200) return false
    const data = await response.json()

    trackMarketingEvent('sign_up')

    return data.redirectUrl
  }

  /* STEP Selection */
  prepareErrorModal() {
    this.$stepUploadFailed.classList.remove('hidden')
    this.$stepAddEmail.classList.add('hidden')
  }

  continueUploadSteps() {
    this.$stepUploadFailed.classList.add('hidden')
    this.$stepAddEmail.classList.remove('hidden')
  }

  showLastStep() {
    this.$stepAddEmail.classList.add('hidden')
    this.$stepUploadData.classList.remove('hidden')
  }

  /* Prepare event listeners */
  fillEventListeners() {
    document
      .getElementById('modal__close-upload')
      .addEventListener('click', this.resetUploadModalSettings.bind(this))
    this.$formConfirmEmail &&
      this.$formConfirmEmail.addEventListener('submit', this.sendUploadDetails.bind(this))

    const modalInput = document.getElementById('modalUploadFileInput')
    document.getElementById('modal_upload__button-retry').addEventListener('click', e => {
      e.preventDefault()
      modalInput.click()
    })
  }

  visibleScreen() {
    if (!Array.from(this.$stepUploadFailed.classList).includes('hidden'))
      return { screen: 'upload_failed' }
    if (!Array.from(this.$stepAddEmail.classList).includes('hidden')) return { screen: 'email' }

    return { screen: 'uploading' }
  }

  getLabelAndBlock() {
    const { page_id: label, block } = this.$modalUploadContainer.dataset || {}
    return { label, block }
  }

  fillHomepageEventListeners() {
    let global = this

    const uploadButtons = [
      [
        document.getElementById('homepageUploadButton'),
        document.getElementById('homepageUploadInput'),
        'hero',
      ],
      [
        document.getElementById('homepageStickyUploadButton'),
        document.getElementById('homepageStickyUploadInput'),
        'sticky',
      ],
      [
        document.getElementById('builderUploadButton'),
        document.getElementById('builderUploadInput'),
        'hero',
      ],
    ].filter(([btn, input]) => btn && input)

    uploadButtons.forEach(([button, input, block]) => {
      button.addEventListener('click', e => {
        e.preventDefault()
        input.click()
        this.$modalUploadContainer.dataset.block = block
        trackInternalEvent('click_upload_resume_website', this.getLabelAndBlock())
      })
    })

    const fileInputs = document.getElementsByClassName('file_upload_input')
    for (let i = 0; i < fileInputs.length; i++) {
      fileInputs[i].addEventListener('change', e => {
        e.preventDefault()
        // check the file type and size if they are ok
        const file = e.target.files[0] || undefined

        let fileUploadChecker = this.checkFile(file)
        if (fileUploadChecker.success) {
          this.fileData = file

          trackInternalEvent('upload_resume_success', this.getLabelAndBlock())
          this.continueUploadSteps()
        } else {
          trackInternalEvent('upload_resume_error', {
            ...this.getLabelAndBlock(),
            error: fileUploadChecker.message || '',
          })
          this.prepareErrorModal()
        }

        // after all checked open Modal
        global.openModal()
      })
    }
  }
}

ready(function () {
  new UploadModalSettings()
})
